import React, { useEffect, useState } from "react";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import Section1AR from "../../components/LandingPage/Section1AR/Section1AR";
import Section3 from "../../components/LandingPage/Section3/Section3";
import "./Employers.css";
import "./InterviewSim.css";
import Slider from "react-slick";
import { Helmet } from 'react-helmet';
import { userService } from "../../_services";

// Interview Simulator Form Component
function InterviewSimulatorForm() {
  const [formData, setFormData] = useState({
    jobRole: '',
    email: '',
    resume: null,
    linkedinUrl: '',
    customQuestions: '',
    demoPackSize: 1,
    demoPackNote: 'Interview simulator demo pack',
    scheduleType: 'now', // 'now' or 'scheduled'
    scheduledDate: '',
    scheduledTime: '',
  });
  const [resumeName, setResumeName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, resume: file }));
      setResumeName(file.name);
    }
  };

  const readFileAsDataURL = (file: File) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string) || '');
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const readFileAsText = (file: File) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string) || '');
    reader.onerror = reject;
    reader.readAsText(file);
  });

  const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setIsSubmitting(true);
      
      try {
          // Read resume locally so the backend can store the file payload with the interview submission.
          let resumeUrl = null;
          let resumeFileName = '';
          let resumeFileType = '';
          let resumeFileSize = 0;
          let resumeData = '';
          let resumeText = '';

          if (formData.resume) {
              const resumeFile = formData.resume as File;
              if (resumeFile.size > 10 * 1024 * 1024) {
                  throw new Error('Resume must be under 10MB');
              }

              resumeFileName = resumeFile.name;
              resumeFileType = resumeFile.type;
              resumeFileSize = resumeFile.size;
              resumeData = await readFileAsDataURL(resumeFile);

              if (resumeFile.name.toLowerCase().endsWith('.txt')) {
                  resumeText = await readFileAsText(resumeFile);
              }
          }

          const customQuestions = formData.customQuestions
              .split('\n')
              .map(question => question.trim())
              .filter(Boolean);
          
          // Submit to backend
          const response = await userService.aiRequest({
              method: 'submit-interview-simulator',
              data: {
                  jobRole: formData.jobRole,
                  email: formData.email,
                  resumeUrl: resumeUrl,
                  resumeFileName,
                  resumeFileType,
                  resumeFileSize,
                  resumeData,
                  resumeText,
                  linkedinUrl: formData.linkedinUrl.trim(),
                  customQuestions,
                  demoPackSize: formData.demoPackSize,
                  demoPackNote: formData.demoPackNote,
                  scheduleType: formData.scheduleType,
                  scheduledDate: formData.scheduledDate,
                  scheduledTime: formData.scheduledTime,
              }
          });
          
          if (response) {
              setIsSubmitting(false);
              setSubmitted(true);
          } else {
              throw new Error(response.error || 'Submission failed');
          }
      } catch (error) {
          console.error('Error submitting form:', error);
          setIsSubmitting(false);
          // Show error message to user
          alert('Failed to submit. Please try again.');
      }
  };

  if (submitted) {
    return (
      <div className="interview-form-card submitted">
        <div className="success-icon">✓</div>
        <h3>You're All Set!</h3>
        <p>
          {formData.scheduleType === 'now' 
            ? "Check your email - your interview link is on its way!"
            : `Your interview is scheduled for ${formData.scheduledDate} at ${formData.scheduledTime}. We'll send you a reminder!`
          }
        </p>
        <button 
          className="btn-secondary"
          onClick={() => setSubmitted(false)}
        >
          Schedule Another Interview
        </button>
      </div>
    );
  }

  return (
    <div className="interview-form-card">
      <div className="form-header">
        <span className="form-badge">🎯 AI-Powered</span>
        <h3>Start Your Mock Interview</h3>
        <p>Get interview-ready in 30 minutes</p>
      </div>
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Job Role You're Applying For *</label>
          <input
            type="text"
            name="jobRole"
            placeholder="e.g. Senior Software Engineer, Marketing Manager"
            value={formData.jobRole}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Upload Your Resume *</label>
          <div className="file-upload-area">
            <input
              type="file"
              id="resume-upload"
              accept=".pdf,.doc,.docx,.txt"
              onChange={handleFileChange}
              required
            />
            <label htmlFor="resume-upload" className="file-upload-label">
              {resumeName ? (
                <span className="file-selected">📄 {resumeName}</span>
              ) : (
                <>
                  <span className="upload-icon">📤</span>
                  <span>Drop your resume here or click to browse</span>
                  <span className="file-types">PDF, DOC, DOCX, TXT (Max 10MB)</span>
                </>
              )}
            </label>
          </div>
        </div>

        <div className="form-group">
          <label>LinkedIn Profile URL <span style={{ opacity: 0.6 }}>(optional)</span></label>
          <input
            type="url"
            name="linkedinUrl"
            placeholder="https://www.linkedin.com/in/your-profile"
            value={formData.linkedinUrl}
            onChange={handleInputChange}
          />
          <span className="input-hint">We'll store the LinkedIn profile scrape for interview personalization.</span>
        </div>

        <div className="form-group">
          <label>Email Address *</label>
          <input
            type="email"
            name="email"
            placeholder="your@email.com"
            value={formData.email}
            onChange={handleInputChange}
            required
          />
          <span className="input-hint">We'll send your detailed performance report here</span>
        </div>

        <div className="form-group">
          <label>Employer Questions <span style={{ opacity: 0.6 }}>(optional)</span></label>
          <textarea
            name="customQuestions"
            placeholder="Paste one interview question per line. Jenny will ask one question at a time, exactly as written."
            value={formData.customQuestions}
            onChange={handleInputChange}
            rows={5}
          />
          <span className="input-hint">Use this when an employer wants to upload their own question set.</span>
        </div>

        <div className="form-group">
          <label>Demo Usage Pack</label>
          <input
            type="number"
            name="demoPackSize"
            min="1"
            max="100"
            value={formData.demoPackSize}
            onChange={handleInputChange}
          />
          <span className="input-hint">Grant a pack of interview credits for demo or sales purposes.</span>
        </div>

        <div className="form-group">
          <label>When would you like to interview?</label>
          <div className="schedule-options">
            <label className={`schedule-option ${formData.scheduleType === 'now' ? 'active' : ''}`}>
              <input
                type="radio"
                name="scheduleType"
                value="now"
                checked={formData.scheduleType === 'now'}
                onChange={handleInputChange}
              />
              <span className="option-content">
                <span className="option-icon">⚡</span>
                <span className="option-text">
                  <strong>Start Now</strong>
                  <small>Get your link immediately</small>
                </span>
              </span>
            </label>
            <label className={`schedule-option ${formData.scheduleType === 'scheduled' ? 'active' : ''}`}>
              <input
                type="radio"
                name="scheduleType"
                value="scheduled"
                checked={formData.scheduleType === 'scheduled'}
                onChange={handleInputChange}
              />
              <span className="option-content">
                <span className="option-icon">📅</span>
                <span className="option-text">
                  <strong>Schedule</strong>
                  <small>Pick your perfect time</small>
                </span>
              </span>
            </label>
          </div>
        </div>

        {formData.scheduleType === 'scheduled' && (
          <div className="schedule-datetime">
            <div className="form-group half">
              <label>Date</label>
              <input
                type="date"
                name="scheduledDate"
                value={formData.scheduledDate}
                onChange={handleInputChange}
                min={new Date().toISOString().split('T')[0]}
                required={formData.scheduleType === 'scheduled'}
              />
            </div>
            <div className="form-group half">
              <label>Time</label>
              <input
                type="time"
                name="scheduledTime"
                value={formData.scheduledTime}
                onChange={handleInputChange}
                required={formData.scheduleType === 'scheduled'}
              />
            </div>
          </div>
        )}

        <button 
          type="submit" 
          className="btn-primary submit-btn"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <span className="loading">Preparing Your Interview...</span>
          ) : formData.scheduleType === 'now' ? (
            <>Start Interview Now <span>→</span></>
          ) : (
            <>Schedule My Interview <span>📅</span></>
          )}
        </button>
      </form>

      <div className="form-footer">
        <span>🔒 Your data is encrypted and never shared</span>
      </div>
    </div>
  );
}

// Stats Card Component
function StatCard({ icon, value, label }) {
  return (
    <div className="stat-card">
      <span className="stat-icon">{icon}</span>
      <span className="stat-value">{value}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}

// How It Works Step Component
function HowItWorksStep({ number, title, description, icon }) {
  return (
    <div className="how-it-works-step">
      <div className="step-number">{number}</div>
      <div className="step-icon">{icon}</div>
      <h4>{title}</h4>
      <p>{description}</p>
    </div>
  );
}

// Testimonial Card Component
function TestimonialCard({ quote, name, role, company, avatar }) {
  return (
    <div className="testimonial-card">
      <div className="quote-mark">"</div>
      <p className="testimonial-quote">{quote}</p>
      <div className="testimonial-author">
        <div className="author-avatar">{avatar}</div>
        <div className="author-info">
          <strong>{name}</strong>
          <span>{role} at {company}</span>
        </div>
      </div>
    </div>
  );
}

export default function InterviewSimulator() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const featuresData = [
    {
      heading: (
        <>
          <div className="mr-2">AI-Powered Personalized Questions</div>
          <img src="/assets/images/employeesLandingPage/exploreFirst.svg" alt="" className="iconImage" />
        </>
      ),
      description: (
        <div>
          Our AI analyzes your <strong>resume and target role</strong> to generate 15 highly relevant interview questions. 
          No generic questions here – every question is tailored to your specific experience, skills, and the job you're pursuing.
          <br /><br />
          From behavioral questions to technical deep-dives, you'll face the exact scenarios real interviewers ask.
        </div>
      ),
    },
    {
      heading: (
        <>
          <div className="mr-2">Real-Time Voice Interaction</div>
          <img src="/assets/images/employersLandingPage/briefcase.svg" alt="" className="iconImage" />
        </>
      ),
      description: (
        <div>
          Practice with our <strong>lifelike AI interviewer</strong> using voice-to-voice conversation. 
          Experience the pressure of a real interview with natural follow-up questions, time constraints, 
          and the need to think on your feet. Choose from multiple interviewer personalities – 
          from friendly and conversational to challenging and rigorous.
        </div>
      ),
    },
    {
      heading: (
        <>
          <div className="mr-2">Comprehensive Performance Report</div>
          <img src="/assets/images/employeesLandingPage/exploreFourth.svg" alt="" className="iconImage" />
        </>
      ),
      description: (
        <div>
          Within minutes of completing your interview, receive a <strong>detailed email report</strong> including:
          <ul style={{ paddingLeft: "30px", marginTop: "10px" }}>
            <li>Overall performance score and confidence rating</li>
            <li>Question-by-question analysis with improvement suggestions</li>
            <li>STAR method evaluation for behavioral responses</li>
            <li>Communication clarity and filler word analysis</li>
            <li>Personalized tips to nail your real interview</li>
          </ul>
        </div>
      ),
    },
    {
      heading: (
        <>
          <div className="mr-2">Industry-Specific Interview Modes</div>
          <img src="/assets/images/employersLandingPage/hammer.svg" alt="" className="iconImage" />
        </>
      ),
      description: (
        <div>
          <ul style={{ paddingLeft: "30px" }}>
            <li><strong>Tech & Engineering:</strong> System design, coding concepts, technical problem-solving</li>
            <li><strong>Sales & Marketing:</strong> Pitch scenarios, metrics discussions, campaign strategies</li>
            <li><strong>Management & Leadership:</strong> Team conflict resolution, strategic decision-making</li>
            <li><strong>Finance & Consulting:</strong> Case studies, analytical reasoning, market sizing</li>
            <li><strong>Healthcare & Education:</strong> Scenario-based questions, ethical dilemmas</li>
          </ul>
        </div>
      ),
    },
  ];

  const [accordionData, setAccordionData] = useState(() => {
    return window.innerWidth < 768 ? featuresData.slice(0, 3) : featuresData;
  });

  const handleResetData = (slice = false) => {
    if (slice) {
      setAccordionData(featuresData.slice(0, 3));
      setExpanded(false);
    } else {
      setAccordionData(featuresData);
      setExpanded(true);
    }
  };

  const testimonials = [
    {
      quote: "I was terrified of interviews. After 3 practice sessions with MPLOI, I walked into my Google interview with complete confidence. Got the offer!",
      name: "Sarah Chen",
      role: "Software Engineer",
      company: "Google",
      avatar: "👩‍💻"
    },
    {
      quote: "The feedback report was incredibly detailed. It pointed out filler words I didn't even know I was using. Game changer for my communication skills.",
      name: "Marcus Johnson",
      role: "Product Manager",
      company: "Stripe",
      avatar: "👨‍💼"
    },
    {
      quote: "As a career changer, I needed extra practice. MPLOI helped me frame my transferable skills perfectly. Landed my dream marketing role!",
      name: "Emily Rodriguez",
      role: "Marketing Director",
      company: "HubSpot",
      avatar: "👩‍🎨"
    },
  ];

  const sliderSettings = {
    dots: true,
    infinite: true,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    speed: 500,
    autoplaySpeed: 5000,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 2 } },
      { breakpoint: 768, settings: { slidesToShow: 1 } },
    ],
  };

  return (
    <div className="employersPageWrap interview-simulator-page">
      <Helmet>
        <title>MPLOI: AI Interview Simulator - Practice & Perfect Your Interview Skills</title>
        <meta name="description" content="Practice job interviews with AI. Get personalized questions based on your resume and target role, plus detailed feedback to help you land your dream job." />
      </Helmet>
      
      <Header />

      {/* Hero Section */}
      <Section1AR
        heading={
          <div className="fw-bold heading">
            Ace Your Next Interview with<br />
            <span className="px-2" style={{ background: "#9256E8" }}>
              AI-Powered
            </span><br />
            Interview Practice<br />
          </div>
        }
        subHeading="Stop wondering if you're ready. Our AI interviewer simulates real job interviews with 15 personalized questions based on your resume and target role. Get detailed feedback and actionable tips delivered straight to your inbox."
        buttons={
          <>
            <a href="#interview-form" className="textBlack">
              <button className="border-0 px-4 py-2 primaryButton">
                Start Practice Interview
                <img src="assets/images/employersLandingPage/teamMembers.svg" alt="" />
              </button>
            </a>
            <a href="#how-it-works">
              <button className="border-0 px-4 py-2 secondaryButton">
                See How It Works
                <img src="assets/images/employeesLandingPage/exploreIcon.svg" alt="" />
              </button>
            </a>
          </>
        }
        image={<InterviewSimulatorForm />}
        mobileImage={<InterviewSimulatorForm />}
      />

      {/* Social Proof Stats */}
      <div className="stats-section">
        <div className="stats-container">
          <StatCard icon="🎯" value="50,000+" label="Interviews Completed" />
          <StatCard icon="✅" value="89%" label="Users Got Job Offers" />
          <StatCard icon="⭐" value="4.9/5" label="Average Rating" />
          <StatCard icon="🏢" value="500+" label="Companies Represented" />
        </div>
      </div>

      {/* How It Works Section */}
      <div id="how-it-works" className="how-it-works-section">
        <div className="section-header">
          <span className="section-badge">Simple Process</span>
          <h2>How It Works</h2>
          <p>From signup to job offer in 4 easy steps</p>
        </div>
        
        <div className="how-it-works-grid">
          <HowItWorksStep
            number="1"
            icon="📝"
            title="Enter Your Target Role"
            description="Tell us the job title you're interviewing for. Our AI analyzes thousands of real interview questions for that specific role."
          />
          <HowItWorksStep
            number="2"
            icon="📄"
            title="Upload Your Resume"
            description="Our AI studies your experience, skills, and achievements to create questions that probe your unique background."
          />
          <HowItWorksStep
            number="3"
            icon="🎤"
            title="Complete Your Interview"
            description="Answer 15 personalized questions via voice or text. Our AI adapts follow-ups based on your responses, just like a real interviewer."
          />
          <HowItWorksStep
            number="4"
            icon="📊"
            title="Get Your Report"
            description="Receive a comprehensive performance analysis with scores, insights, and actionable tips to improve before your real interview."
          />
        </div>

        <div className="how-it-works-cta">
          <a href="#interview-form" className="textBlack">
            <button className="border-0 px-5 py-3 primaryButton">
              Try It Free Now
              <span style={{ marginLeft: "8px" }}>→</span>
            </button>
          </a>
        </div>
      </div>

      {/* What You'll Get Section */}
      <div className="report-preview-section">
        <div className="section-header">
          <span className="section-badge">Detailed Feedback</span>
          <h2>Your Personalized Interview Report</h2>
          <p>Here's what you'll receive after every practice session</p>
        </div>

        <div className="report-preview-container">
          <div className="report-preview-card">
            <div className="report-preview-header">
              <span className="report-icon">📊</span>
              <h4>Performance Dashboard</h4>
            </div>
            <div className="report-preview-content">
              <div className="score-circle">
                <span className="score">87</span>
                <span className="score-label">Overall Score</span>
              </div>
              <div className="score-breakdown">
                <div className="score-item">
                  <span>Communication Clarity</span>
                  <div className="score-bar"><div style={{ width: "92%" }}></div></div>
                  <span>92%</span>
                </div>
                <div className="score-item">
                  <span>Technical Accuracy</span>
                  <div className="score-bar"><div style={{ width: "85%" }}></div></div>
                  <span>85%</span>
                </div>
                <div className="score-item">
                  <span>STAR Method Usage</span>
                  <div className="score-bar"><div style={{ width: "78%" }}></div></div>
                  <span>78%</span>
                </div>
                <div className="score-item">
                  <span>Confidence Level</span>
                  <div className="score-bar"><div style={{ width: "88%" }}></div></div>
                  <span>88%</span>
                </div>
              </div>
            </div>
          </div>

          <div className="report-features-list">
            <div className="report-feature">
              <span className="feature-icon">✓</span>
              <div>
                <strong>Question-by-Question Analysis</strong>
                <p>Detailed breakdown of each answer with specific improvement suggestions</p>
              </div>
            </div>
            <div className="report-feature">
              <span className="feature-icon">✓</span>
              <div>
                <strong>Filler Word Detection</strong>
                <p>Track "um," "like," "you know" usage with tips to eliminate them</p>
              </div>
            </div>
            <div className="report-feature">
              <span className="feature-icon">✓</span>
              <div>
                <strong>Answer Length Optimization</strong>
                <p>Learn when you're being too brief or rambling too long</p>
              </div>
            </div>
            <div className="report-feature">
              <span className="feature-icon">✓</span>
              <div>
                <strong>Sample "Perfect Answers"</strong>
                <p>See AI-generated ideal responses to compare against yours</p>
              </div>
            </div>
            <div className="report-feature">
              <span className="feature-icon">✓</span>
              <div>
                <strong>Personalized Action Plan</strong>
                <p>5 specific things to practice before your real interview</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Banner */}
      <div className="section2">
        <div className="heading">
          Don't leave your dream job to chance.<br />
          Practice until perfect with AI that knows<br />
          exactly what interviewers are looking for.
        </div>
        <div className="sec-2-btn">
          <a href="#interview-form" className="textBlack">
            <button className="border-0 px-5 py-2 primaryButton">
              Start Your Interview Now
              <img src="assets/images/employeesLandingPage/testYourself.svg" alt="" />
            </button>
          </a>
        </div>
      </div>

      {/* Features Accordion */}
      <div id="employers-feature">
        <Section3
          accrodianData={accordionData}
          tagName="Powerful Features"
          description="Everything You Need to Interview with Confidence"
          image={
            <img
              width="650"
              src="/assets/images/lp_images/interview_simulator/interview_hero.png"
              className="img-fluid"
              alt="AI Interview Simulator"
            />
          }
          mobilebutton={
            isMobile ? (
              expanded ? (
                <button
                  className="border-0 px-5 py-2 primaryButton textBlack"
                  onClick={() => handleResetData(true)}
                >
                  Show Less
                  <img src="assets/images/downArrowBlack.svg" alt="" style={{ transform: "rotate(180deg)" }} />
                </button>
              ) : (
                <button
                  className="border-0 px-5 py-2 primaryButton textBlack"
                  onClick={() => handleResetData()}
                >
                  Show All Features
                  <img src="assets/images/downArrowBlack.svg" alt="" />
                </button>
              )
            ) : null
          }
        />
      </div>

      {/* Testimonials */}
      <div className="testimonials-section">
        <div className="section-header">
          <span className="section-badge">Success Stories</span>
          <h2>Join Thousands Who Landed Their Dream Jobs</h2>
        </div>
        <div className="testimonials-slider">
          <Slider {...sliderSettings}>
            {testimonials.map((testimonial, index) => (
              <TestimonialCard key={index} {...testimonial} />
            ))}
          </Slider>
        </div>
      </div>

      {/* Sample Questions Section */}
      <div className="sample-questions-section">
        <div className="section-header">
          <span className="section-badge">Preview</span>
          <h2>Sample Interview Questions You Might Face</h2>
          <p>Based on real interviews at top companies</p>
        </div>

        <div className="questions-grid">
          <div className="question-card behavioral">
            <span className="question-type">Behavioral</span>
            <p>"Tell me about a time you had to deal with a difficult team member. How did you handle it?"</p>
            <span className="question-source">Asked at Amazon, Microsoft, Meta</span>
          </div>
          <div className="question-card technical">
            <span className="question-type">Technical</span>
            <p>"Walk me through how you would design a URL shortening service like bit.ly."</p>
            <span className="question-source">Asked at Google, Uber, LinkedIn</span>
          </div>
          <div className="question-card situational">
            <span className="question-type">Situational</span>
            <p>"If you discovered your manager was making an unethical decision, what would you do?"</p>
            <span className="question-source">Asked at Goldman Sachs, McKinsey</span>
          </div>
          <div className="question-card leadership">
            <span className="question-type">Leadership</span>
            <p>"Describe a project where you had to influence stakeholders without direct authority."</p>
            <span className="question-source">Asked at Apple, Salesforce, Netflix</span>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="faq-section">
        <div className="section-header">
          <span className="section-badge">FAQ</span>
          <h2>Frequently Asked Questions</h2>
        </div>

        <div className="faq-grid">
          <div className="faq-item">
            <h4>How long does an interview session take?</h4>
            <p>A typical session with 15 questions takes about 25-35 minutes, similar to a real first-round interview.</p>
          </div>
          <div className="faq-item">
            <h4>Can I practice multiple times for the same role?</h4>
            <p>Absolutely! Each session generates fresh questions so you can practice as many times as you need.</p>
          </div>
          <div className="faq-item">
            <h4>Is my resume data secure?</h4>
            <p>Yes, we use bank-level encryption and never share your data. Resumes are automatically deleted after 30 days.</p>
          </div>
          <div className="faq-item">
            <h4>What if I want to reschedule my interview?</h4>
            <p>No problem! You'll receive a link to reschedule in your confirmation email. Reschedule anytime up to 5 minutes before.</p>
          </div>
          <div className="faq-item">
            <h4>Do I need any special equipment?</h4>
            <p>Just a device with a microphone and internet connection. Works on desktop, tablet, or mobile.</p>
          </div>
          <div className="faq-item">
            <h4>How quickly will I receive my report?</h4>
            <p>Your detailed performance report is emailed within 5 minutes of completing your interview.</p>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div id="interview-form" className="final-cta-section">
        <div className="final-cta-content">
          <h2>Ready to Crush Your Next Interview?</h2>
          <p>Join 50,000+ job seekers who practiced with MPLOI and landed offers at their dream companies.</p>
          <div className="company-logos">
            <span>Trusted by candidates hired at:</span>
            <div className="logos-row">
              <span className="company-logo">Google</span>
              <span className="company-logo">Amazon</span>
              <span className="company-logo">Microsoft</span>
              <span className="company-logo">Meta</span>
              <span className="company-logo">Apple</span>
            </div>
          </div>
        </div>
        <div className="final-cta-form">
          <InterviewSimulatorForm />
        </div>
      </div>

      <Footer />
    </div>
  );
}